import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2 } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import {
  useGetCompanies,
  useGetSlackChannels,
} from '../../../hooks/use-hydra/admin';
import { FormInput, FormLabel } from '../../Form';
import { Dropdown } from '../../elements/Dropdown';
import Button from '../../elements/Button';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { addCompany } from '../../../api/admin';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  padding-bottom: 72px;
`;
const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  /* gap: 16px; */
  flex: 1;
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

export default function AddCompanyModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { getTokenSilently } = useAuth();
  const { data: slackChannels } = useGetSlackChannels();
  const { refetch: refetchCompanies, isLoading: isLoadingCompanies } =
    useGetCompanies();

  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [discussChannel, setDiscussChannel] =
    React.useState<{
      label: string;
      value: string;
    } | null>(null);
  const [statusChannel, setStatusChannel] =
    React.useState<{
      label: string;
      value: string;
    } | null>(null);

  const canSubmit = name && slug && discussChannel && statusChannel;
  const submitTooltip = !canSubmit ? 'Please fill in all fields' : '';

  const addCompanyMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const response = await addCompany(token, {
        name,
        slug,
        slackDiscussChannelId: discussChannel?.value ?? '',
        slackStatusChannelId: statusChannel?.value ?? '',
      });
      return response;
    },
    onSuccess: async () => {
      toast.success('Company added successfully');
      await refetchCompanies();
      onClose();
    },
    onError: (error: Error | AxiosError) => {
      const errorMessage = error?.message;
      toast.error(`Failed to add company - ${errorMessage}`);
    },
  });

  const onSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSlug(
      value
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^a-z0-9-]/g, '')
    );
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const onAddCompany = () => {
    addCompanyMutation.mutate();
  };

  const discussChannelOptions = slackChannels?.items.map((channel) => ({
    label: `#${channel.name}`,
    value: channel.id,
  }));

  const statusChannelOptions = slackChannels?.items.map((channel) => ({
    label: `#${channel.name}`,
    value: channel.id,
  }));

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Add New Company</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <Row>
          <Column>
            <FormLabel>Company Name</FormLabel>
            <FormInput
              placeholder="Company Name"
              value={name}
              onChange={onNameChange}
            />
          </Column>
          <Column>
            <FormLabel>Company Slug</FormLabel>
            <FormInput
              placeholder="Company Slug"
              value={slug}
              onChange={onSlugChange}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <FormLabel>Discuss Channel</FormLabel>
            <Dropdown
              options={discussChannelOptions}
              onChange={setDiscussChannel}
              value={discussChannel}
              placeholder="Select #discuss Channel"
              isValidNewOption={() => false}
            />
          </Column>
          <Column>
            <FormLabel>Status Channel</FormLabel>
            <Dropdown
              options={statusChannelOptions}
              onChange={setStatusChannel}
              value={statusChannel}
              placeholder="Select #status Channel"
              isValidNewOption={() => false}
            />
          </Column>
        </Row>
        <Row>
          <Button
            isLoading={addCompanyMutation.isLoading || isLoadingCompanies}
            label="Add Company"
            faIcon="fa-solid fa-plus"
            showIconBeforeLabel
            type="secondary"
            isDisabled={!canSubmit || addCompanyMutation.isLoading}
            tooltip={submitTooltip}
            onClick={onAddCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}
